import QRCodeStyling from "qr-code-styling";
import { useEffect, useRef, useState } from "react";

import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { IconActionButton } from "Components/Shared/Buttons/IconActionButton";
import { useIsMobile } from "Hooks/useIsMobile";
import { Resources, useResource } from "Translations/Resources";
import { isFileSharingAvailable, shareFile } from "Utils/ShareUtils";
import { Box, Stack } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShareIcon from "@mui/icons-material/Share";
import { toast } from "sonner";

export type QRCodeVariant = "default" | "compact";

type Props = {
  content: string;
  size?: number;
  downloadFileName?: string;
  variant?: QRCodeVariant;
  shareButtonLabel?: string;
};

export const QRCode = ({
  content,
  size = 250,
  downloadFileName = "QR",
  variant = "default",
  shareButtonLabel,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [qrCode] = useState<QRCodeStyling>(
    new QRCodeStyling({
      width: size,
      height: size,
      data: content,
      margin: 0,
      qrOptions: {
        typeNumber: 0,
        mode: "Byte",
        errorCorrectionLevel: "H",
      },
      imageOptions: {
        hideBackgroundDots: false,
        imageSize: 0.4,
        margin: 0,
      },
      dotsOptions: { type: "rounded", color: "#000000", gradient: undefined },
      backgroundOptions: { color: "#ffffff" },
      image: "/qr-logo.svg",
      cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
      cornersDotOptions: { type: "dot", color: "#000000" },
      type: "svg",
    }),
  );

  const { t } = useResource();

  useEffect(() => {
    qrCode.append(ref.current as any);
  }, [qrCode]);

  useEffect(() => {
    // The QR code throws an error when the canvas is too small. We don't care too much, but we sill want to catch it before sentry.
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        event.reason &&
        typeof event.reason === "string" &&
        event.reason.includes("canvas is too small")
      ) {
        console.warn(
          "QR Code canvas error caught by global handler:",
          event.reason,
        );
        event.preventDefault();
        return;
      }
      console.error("Unhandled promise rejection:", event.reason);
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () =>
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
  }, []);

  const handleShare = async () => {
    const data = await qrCode.getRawData("png").catch(() => null);
    if (!(data instanceof Blob)) {
      toast.error(t(Resources.Components.ShareButton.Error));
      return;
    }

    const file = new File([data], `${downloadFileName}.png`, {
      type: "image/png",
    });
    if ((await shareFile(file)) === "failed") {
      toast.error(t(Resources.Components.ShareButton.Error));
    }
  };

  return (
    <Stack
      alignItems="center"
      gap={variant === "compact" ? 0 : 5}
      m={variant === "compact" ? 0 : 3} //TODO (Nice to have): remove the margin from here and implement it where the QRCode is used
    >
      <div ref={ref} />
      {shareButtonLabel ? (
        // Payment QRs: transaction-detail-style action row (icon above label).
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          width="100%"
        >
          <IconActionButton
            Icon={FileDownloadIcon}
            label={t(Resources.Common.DownloadQRCode)}
            onClick={() =>
              qrCode.download({ extension: "png", name: downloadFileName })
            }
          />
          {isFileSharingAvailable && isMobile && (
            <IconActionButton
              Icon={ShareIcon}
              label={shareButtonLabel}
              onClick={handleShare}
            />
          )}
        </Box>
      ) : (
        <BlTextButton
          startIcon={<FileDownloadIcon />}
          onClick={() =>
            qrCode.download({ extension: "png", name: downloadFileName })
          }
        >
          {t(Resources.Common.DownloadQRCode)}
        </BlTextButton>
      )}
    </Stack>
  );
};
